import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";
import { getPracticeId, isPractice9928, practiceFaxProfile } from "../config/practice";

/*
 * Communication > Fax calls, per practice (10-05):
 *   listFaxes       GET  /v1/{pid}/fax/get-list   v2, pinned
 *   refreshFaxes    POST /v1/{pid}/fax/refresh    v2, pinned (profile.refresh)
 *   sendFax         POST /v1/{pid}/fax/send       v2, pinned (profile.sendRoute
 *                   "v2"), or 9928's v1 POST /v1/fax/send (C-HIM's account)
 *   fax documents   GET  /v1/{pid}/fax/{fax_id}/document (below)
 *   listAllFaxes    GET  /v1/{pid}/fax/all        v2 (10-22): open + done + deleted, 30 days
 *   deleteFax       POST /v1/{pid}/fax/delete     v2, pinned (10-12: was v1;
 *                   v2 logs who / when / reason itself)
 *   listDeletedFaxes GET /v1/{pid}/fax/deleted     v2, pinned (the Deleted view)
 *   restoreFax      POST /v1/{pid}/fax/{fax_id}/restore  v2, pinned
 * Every v2 call here is pinned like the forms code: it skips the v1/v2 routing
 * rules, never falls back to v1 and never trips the v2 circuit breaker. The
 * server answers RingCentral / hub failures with 4xx (424), not 502.
 *
 * Fax PDFs through the v2 gateway (security hotfix, 2026-10-05).
 *
 * A fax PDF used to come straight from the hub: check-filepath returned a
 * https://hub.brainymed.com/ringcentral/fax/docs/fax_<message id>.pdf URL (or
 * the hub's /ringcentral/fax/viewdoc/, called without a login, made one), the
 * iframe loaded it with no login, and Send to Athena / Send to EMR downloaded
 * it again through the dallas any-URL proxy. Anyone with a message id could
 * read those files, so the hub's fax folder is being closed to browsers.
 *
 * Every fax PDF now comes from GET /v1/{practiceid}/fax/{fax_id}/document on
 * v2: api.js sends the staff token, the server checks the practice and streams
 * the PDF, and it arrives here as a Blob. The viewer gets an object URL for it
 * (release it with releaseFaxDocumentUrl when the viewer moves on), the two
 * modals a File.
 *
 * {fax_id} is the fax_master row id. The list flattens each row's RingCentral
 * record over the row, so `id` there is the RingCentral message id; fax.jsx
 * keeps the row's own id as `faxid`.
 *
 * Pinned to v2 (`_gw: "v2-pinned"`, as the forms code does): the route exists
 * on v2 only, so it skips the v1/v2 routing rules, never falls back to v1 and
 * never trips the v2 circuit breaker (api/api.js).
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
const MULTIPART = { "Content-Type": "multipart/form-data" };

const practiceId = () => getPracticeId();
const enc = (s) => encodeURIComponent(String(s));
const isBlob = (v) => typeof Blob !== "undefined" && v instanceof Blob;
const blobText = (b) =>
  typeof b.text === "function"
    ? b.text()
    : new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsText(b);
      });

const NOT_AVAILABLE = "Fax document not available";
const ROUTER_MISS = ["Not Found", "Method Not Allowed"];
// Errors this module raises itself (their text is shown as is).
const faxError = (message) => Object.assign(new Error(message), { fax: true });

// The id the document route takes for a list row, or null.
export function faxDocumentId(fax) {
  const id = fax && fax.faxid;
  return id === undefined || id === null || id === "" ? null : id;
}

export function faxDocumentPath(fax) {
  const id = faxDocumentId(fax);
  return id === null ? null : `/v1/${practiceId()}/fax/${enc(id)}/document`;
}

// Text to show for a failed load: the server's message for a 4xx (a Blob for
// this blob request), this module's own, else `fallback`.
export async function faxErrorMessage(error, fallback = "Could not load the fax document") {
  const status = error && error.response && error.response.status;
  let d = status >= 400 && status < 500 ? error.response.data : null;
  try {
    if (isBlob(d)) d = JSON.parse(await blobText(d));
  } catch (e) {
    d = null;
  }
  // FastAPI's own {"detail": "Not Found"} / "Method Not Allowed" means the
  // route is not on this v2 yet (a UI deployed before the gateway): show the
  // generic line, not "Not Found".
  if (d && Object.keys(d).length === 1 && ROUTER_MISS.includes(d.detail)) return fallback;
  const detail = d && (d.message || d.detail);
  if (typeof detail === "string" && detail) return detail;
  if (error && error.fax && error.message) return error.message;
  return fallback;
}

// "1-2,5" for page numbers [1, 2, 5] (sorted, unique): the document route's
// ?pages= (v2 pages.py: a deterministic cut, so filing the same pages twice
// sends the same bytes and the Athena duplicate guard recognises them).
export function pagesSpec(pages) {
  const p = [...new Set((Array.isArray(pages) ? pages : []).map(Number).filter((n) => Number.isInteger(n) && n > 0))].sort(
    (a, b) => a - b
  );
  const out = [];
  for (let i = 0; i < p.length; ) {
    let j = i;
    while (j + 1 < p.length && p[j + 1] === p[j] + 1) j += 1;
    out.push(j > i ? `${p[i]}-${p[j]}` : String(p[i]));
    i = j + 1;
  }
  return out.join(",");
}

// The fax's PDF as a Blob (type application/pdf); with `pages` (page numbers)
// only those pages (owner 10-13: Send to Athena by page groups).
export async function faxDocumentBlob(fax, { pages } = {}) {
  const base = faxDocumentPath(fax);
  if (!base) throw faxError(NOT_AVAILABLE);
  const spec = pages ? pagesSpec(pages) : "";
  if (pages && !spec) throw faxError(NOT_AVAILABLE);
  const path = spec ? `${base}?pages=${enc(spec)}` : base;
  // api.js resolves to the response body: the Blob itself.
  const data = await api.get(path, { ...V2, responseType: "blob" });
  if (!isBlob(data) || !data.size) throw faxError(NOT_AVAILABLE);
  if (/json/i.test(data.type || "")) {
    // A JSON answer is a refusal ({status, message}), not a PDF.
    let message = NOT_AVAILABLE;
    try {
      const body = JSON.parse(await blobText(data));
      if (body && typeof body.message === "string" && body.message) message = body.message;
    } catch (e) {
      // keep the generic message
    }
    throw faxError(message);
  }
  return data.type === "application/pdf" ? data : new Blob([data], { type: "application/pdf" });
}

// An object URL for the viewer (iframe / new tab). The caller releases it.
export async function faxDocumentUrl(fax) {
  return URL.createObjectURL(await faxDocumentBlob(fax));
}

export function releaseFaxDocumentUrl(url) {
  if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
}

// The fax's PDF as a File, for the Send to Athena / Send to EMR uploads
// (`pages`: only those pages, see faxDocumentBlob).
export async function faxDocumentFile(fax, name, { pages } = {}) {
  const blob = await faxDocumentBlob(fax, pages ? { pages } : undefined);
  return new File([blob], name, { type: "application/pdf" });
}

// ---- AI read --------------------------------------------------------------
// GET /v1/{pid}/fax/{fax_id}/ai (v2-fax-ai, staff only): the fax's full AI
// result, including the suggested chart's name and DOB that the list leaves
// out. Resolves to its `data` ({status, match: {status, confidence,
// suggested, candidates}, ...}) or null (not read yet / no fax_ai table).
// The api client resolves to the response BODY.
export async function faxAiDetail(fax) {
  const id = faxDocumentId(fax);
  if (id === null) return null;
  const body = await api.get(`/v1/${practiceId()}/fax/${enc(id)}/ai`, V2);
  return body && typeof body === "object" && body.data && typeof body.data === "object" ? body.data : null;
}

// ---- list, refresh, send ---------------------------------------------------
const base = () => `/v1/${practiceId()}/fax`;

// The practice's fax rows ({status, data: [{id, extentionid, records, ...}]}).
// With a leading slash: the old slash-less path never matched care.json's v2
// rule, so the list always went to v1.
export function listFaxes() {
  return api.get(`${base()}/get-list`, V2);
}

// get-list / fax/deleted rows as the table uses them: the RingCentral record
// flattened over the row, `faxid` = the row id (the document / work routes),
// and the row's own ai / work / deleted kept.
export function formatFaxRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((f) => ({
    ...f, // keep id, status etc
    ...f.records, // flatten records into root
    extensionId: f.records?.extensionId || f.extentionid,
    // The records' id (the RingCentral message id) replaces the row's own id
    // above; the document route takes the row id.
    faxid: f.id,
    // The row's AI read (faxAi.js), never a records key of that name.
    ...(f.ai !== undefined && { ai: f.ai }),
    // Likewise its work state (faxWork.js): provider, done, latest note.
    ...(f.work !== undefined && { work: f.work }),
    ...(f.deleted === true && { deleted: true }),
  }));
}

// The Deleted view (owner 10-12): faxes received in the last `days` (1-31)
// that staff deleted, each with its work state (who / when / reason).
// {status, days, data: [...rows, deleted: true], fax_work_available}
export function listDeletedFaxes(days = 7) {
  const d = Math.min(31, Math.max(1, Math.round(Number(days)) || 7));
  return api.get(`${base()}/deleted?days=${d}`, V2);
}

// "All (30 days)" (owner 10-22): every fax received in the last `days`
// (1-30) whatever its state: open, done and deleted (`deleted: true`).
// `provider` narrows the rows on the server ("all", a provider id,
// "unassigned", "ai_pending"); provider_counts counts the whole window.
// {status, days, providerid, data, provider_counts, window_total, fax_work_available}
export function listAllFaxes(days = 30, provider = "all") {
  const d = Math.min(30, Math.max(1, Math.round(Number(days)) || 30));
  const p = /^(?:[0-9]{1,9}|unassigned|ai_pending|all)$/.test(String(provider || "")) ? String(provider) : "all";
  return api.get(`${base()}/all?days=${d}&providerid=${enc(p)}`, V2);
}

// Delete (soft: nothing changes in RingCentral). reason: spam | not_ours |
// duplicate | "" ; note optional. Resolves to the body: {status, fax_id,
// work_logged} — work_logged !== true means the server did not write the
// history entry (an older v2), so the caller writes it (faxWorkAct).
export function deleteFax(fax, { reason, note } = {}) {
  const ext = fax?.extensionId || fax?.extentionid;
  const mid = fax?.id;
  if (!ext || mid === undefined || mid === null || mid === "") {
    return Promise.reject(faxError("This fax cannot be deleted"));
  }
  const body = {};
  if (reason) body.reason = reason;
  if (note) body.note = note;
  return api.post(`${base()}/delete?extentionid=${enc(ext)}&messageid=${enc(mid)}`, body, V2);
}

// New faxes from the practice's RingCentral now (the server also runs it on a
// timer): {status, inserted, duplicates, downloaded, download_failed, ...}.
export function refreshFaxes() {
  return api.post(`${base()}/refresh`, {}, V2);
}

// A send id the server de-duplicates on (v2 idempotency_key, 8-64 of
// [A-Za-z0-9_.:-]): a repeat of the same submit is answered, never re-sent.
export function newFaxSendKey() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch (e) {
    // fall through
  }
  return `fx-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Where this practice's Send Fax goes: "v2" (its own RingCentral account),
// "v1" (9928 only: C-HIM's account), or null (no sending from care).
export function faxSendRoute() {
  const route = practiceFaxProfile()?.sendRoute;
  if (route === "v2") return "v2";
  if (route === "v1" && isPractice9928()) return "v1";
  return null;
}

// Send `formData` (to, attachment, cover_page_text). Resolves to the body
// ({result: "true", ...}); a refusal rejects with the server's message.
export function sendFax(formData, { idempotencyKey } = {}) {
  const route = faxSendRoute();
  if (route === "v2") {
    if (idempotencyKey) formData.append("idempotency_key", idempotencyKey);
    return api.post(`${base()}/send`, formData, { ...V2, headers: MULTIPART });
  }
  if (route === "v1") {
    // Multipart, not the api client's default JSON: axios turns FormData into
    // JSON when Content-Type is application/json, so the PDF arrived as {} and
    // every send from care failed with 400 (2026-09-28/29).
    return api.post("/v1/fax/send", formData, { headers: MULTIPART });
  }
  // Never fall back to another practice's account.
  return Promise.reject(faxError("Fax sending is not set up for this practice"));
}

// The send was accepted. The api client resolves to the response BODY
// ({result, details, data}), not the axios response.
export function faxSendOk(res) {
  return res?.result === "true" || (res?.status === 200 && res?.data?.result === "true");
}

// Text for a failed send: the server's message ({message} / FastAPI
// {detail}), this module's own, else `fallback`.
export function faxSendErrorMessage(error, fallback = "Failed to send fax") {
  const d = error?.response?.data;
  if (d && typeof d.message === "string" && d.message) return d.message;
  if (d && typeof d.detail === "string" && d.detail) return d.detail;
  if (d && Array.isArray(d.detail) && d.detail[0]?.msg) return String(d.detail[0].msg);
  if (error?.fax && error.message) return error.message;
  if (!error?.response && error?.message && error.message !== fallback) return error.message;
  return fallback;
}

// ---- work: provider, done / notes, history (v2 work.py, owner 10-11) -------
// All pinned to v2 like the rest of this file. Each resolves to the body:
// { work, history: [{id, action, quick_pick, note, detail, user_name,
// created_at}] (newest first), providers? }.
const workPath = (fax, tail) => {
  const id = faxDocumentId(fax);
  if (id === null) throw faxError("This fax cannot be updated");
  return `/v1/${practiceId()}/fax/${enc(id)}/${tail}`;
};

// {available, work, history, providers}
export function faxWorkDetail(fax) {
  return api.get(workPath(fax, "work"), V2);
}

// action: done | reopen | note | review | unreview | filed_emr | filed_athena | deleted
// (deleted: only as the fallback after an older v2's delete; quickPick = the reason label)
export function faxWorkAct(fax, { action, note, quickPick, patientId } = {}) {
  const body = { action };
  if (note) body.note = note;
  if (quickPick) body.quick_pick = quickPick;
  if (patientId !== undefined && patientId !== null && patientId !== "") body.patientid = Number(patientId);
  return api.post(workPath(fax, "work"), body, V2);
}

// A deleted fax back in the list (logged "restore"); note optional.
export function restoreFax(fax, note) {
  return api.post(workPath(fax, "restore"), note ? { note } : {}, V2);
}

// providerId null = Unassigned
export function faxSetProvider(fax, providerId, note) {
  const body = { providerid: providerId === null || providerId === "" ? null : Number(providerId) };
  if (note) body.note = note;
  return api.put(workPath(fax, "provider"), body, V2);
}

// ---- assignee ("user buckets", owner 10-13; v2 assign.py, migration 009) --
// userId null = Unassigned. Resolves to {work, history}.
export function faxSetAssignee(fax, userId, note) {
  const body = { user_id: userId === null || userId === undefined || userId === "" ? null : Number(userId) };
  if (note) body.note = note;
  return api.put(workPath(fax, "assignee"), body, V2);
}

// Several faxes at once (list selection). faxes: rows (their faxid); resolves
// to {updated, unchanged, skipped: [ids], work: {id: state}}.
export function faxAssignMany(faxes, userId) {
  const ids = (Array.isArray(faxes) ? faxes : []).map(faxDocumentId).filter((id) => id !== null).map(Number);
  if (!ids.length) return Promise.reject(faxError("Pick at least one fax"));
  return api.post(
    `${base()}/assign`,
    { fax_ids: ids, user_id: userId === null || userId === undefined || userId === "" ? null : Number(userId) },
    V2
  );
}

// {available, count}: faxes to work assigned to the signed-in login (side nav).
export function faxAssignedCount() {
  return api.get(`${base()}/assigned-count`, V2);
}

// how: confirmed (the AI's suggestion) | picked
export function faxSetPatient(fax, patientId, how = "confirmed") {
  return api.post(workPath(fax, "patient"), { patientid: Number(patientId), how }, V2);
}

// Text for a refused work call: the server's message, else `fallback`.
export function faxWorkErrorMessage(error, fallback = "Could not save. Please try again.") {
  const d = error?.response?.data;
  if (d && typeof d.message === "string" && d.message) return d.message;
  if (error?.fax && error.message) return error.message;
  return fallback;
}
